import React, { useState, useEffect } from 'react';
import { BarChart3, ChevronLeft, ChevronRight, Loader2 } from 'lucide-react';
import { getResumenMensual } from '../services/api';
import toast from 'react-hot-toast';

function formatCurrency(value) {
  if (value === null || value === undefined) return '0,00 €';
  return new Intl.NumberFormat('es-ES', { style: 'currency', currency: 'EUR' }).format(value);
}

const MONTH_NAMES = ['Enero','Febrero','Marzo','Abril','Mayo','Junio','Julio','Agosto','Septiembre','Octubre','Noviembre','Diciembre'];

export default function ResumenMensual() {
  const now = new Date();
  const [year, setYear] = useState(now.getFullYear());
  const [month, setMonth] = useState(now.getMonth() + 1);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    setLoading(true);
    getResumenMensual(year, month)
      .then(setData)
      .catch((err) => toast.error('Error: ' + err.message))
      .finally(() => setLoading(false));
  }, [year, month]);

  const prev = () => {
    if (month === 1) { setMonth(12); setYear(y => y - 1); }
    else setMonth(m => m - 1);
  };
  const next = () => {
    if (month === 12) { setMonth(1); setYear(y => y + 1); }
    else setMonth(m => m + 1);
  };

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <h1 className="text-2xl font-bold text-gray-900">Resumen Mensual</h1>
        <div className="flex items-center gap-2">
          <button onClick={prev} className="btn-secondary p-2"><ChevronLeft className="w-4 h-4" /></button>
          <span className="text-sm font-medium text-gray-700 min-w-[140px] text-center">
            {MONTH_NAMES[month - 1]} {year}
          </span>
          <button onClick={next} className="btn-secondary p-2"><ChevronRight className="w-4 h-4" /></button>
        </div>
      </div>

      {loading ? (
        <div className="flex items-center justify-center h-64">
          <Loader2 className="w-8 h-8 animate-spin text-primary-600" />
        </div>
      ) : !data ? (
        <p className="text-gray-400 text-center py-12">Sin datos</p>
      ) : (
        <>
          {/* Summary cards */}
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
            <div className="card !p-4 text-center">
              <p className="text-xs text-gray-500">Facturas</p>
              <p className="text-2xl font-bold text-gray-900">{data.num_facturas}</p>
            </div>
            <div className="card !p-4 text-center">
              <p className="text-xs text-gray-500">Base Imponible</p>
              <p className="text-lg font-bold text-gray-900">{formatCurrency(data.total_base)}</p>
            </div>
            <div className="card !p-4 text-center">
              <p className="text-xs text-gray-500">IVA</p>
              <p className="text-lg font-bold text-gray-900">{formatCurrency(data.total_iva)}</p>
            </div>
            <div className="card !p-4 text-center">
              <p className="text-xs text-gray-500">Total</p>
              <p className="text-2xl font-bold text-primary-700">{formatCurrency(data.total)}</p>
            </div>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            {/* By category */}
            <div className="card">
              <div className="flex items-center gap-2 mb-4">
                <BarChart3 className="w-5 h-5 text-primary-600" />
                <h2 className="text-lg font-semibold text-gray-900">Por Categoria</h2>
              </div>
              {(data.por_categoria || []).length > 0 ? (
                <div className="space-y-3">
                  {data.por_categoria.map((cat, i) => {
                    const pct = data.total > 0 ? (cat.total / data.total) * 100 : 0;
                    return (
                      <div key={i}>
                        <div className="flex items-center justify-between text-sm mb-1">
                          <span className="text-gray-700">{cat.categoria}</span>
                          <span className="font-mono font-medium">{formatCurrency(cat.total)}</span>
                        </div>
                        <div className="w-full bg-gray-100 rounded-full h-2">
                          <div className="bg-primary-500 h-2 rounded-full" style={{ width: `${pct}%` }} />
                        </div>
                        <div className="flex justify-between text-xs text-gray-400 mt-0.5">
                          <span>{cat.num_facturas} fact.</span>
                          <span>{pct.toFixed(1)}%</span>
                        </div>
                      </div>
                    );
                  })}
                </div>
              ) : (
                <p className="text-sm text-gray-400">Sin datos</p>
              )}
            </div>

            {/* By provider */}
            <div className="card">
              <div className="flex items-center gap-2 mb-4">
                <BarChart3 className="w-5 h-5 text-green-600" />
                <h2 className="text-lg font-semibold text-gray-900">Por Proveedor</h2>
              </div>
              {(data.por_proveedor || []).length > 0 ? (
                <div className="space-y-3">
                  {data.por_proveedor.map((prov, i) => {
                    const pct = data.total > 0 ? (prov.total / data.total) * 100 : 0;
                    return (
                      <div key={i}>
                        <div className="flex items-center justify-between text-sm mb-1">
                          <span className="text-gray-700">{prov.proveedor}</span>
                          <span className="font-mono font-medium">{formatCurrency(prov.total)}</span>
                        </div>
                        <div className="w-full bg-gray-100 rounded-full h-2">
                          <div className="bg-green-500 h-2 rounded-full" style={{ width: `${pct}%` }} />
                        </div>
                        <div className="flex justify-between text-xs text-gray-400 mt-0.5">
                          <span>{prov.num_facturas} fact.</span>
                          <span>{pct.toFixed(1)}%</span>
                        </div>
                      </div>
                    );
                  })}
                </div>
              ) : (
                <p className="text-sm text-gray-400">Sin datos</p>
              )}
            </div>
          </div>
        </>
      )}
    </div>
  );
}
